export const projects = [
  // =========================================================================
  // 1. ACTIVE / IN-PROGRESS SYSTEMS (TOP)
  // =========================================================================
  {
    id: "coding-club-saas",
    slug: "coding-club-saas",
    title: "Multi-Tenant SaaS College Coding Club Platform",
    shortDescription: "A multi-tenant cloud platform for university coding societies with automated contest management, leaderboards, and member telemetry.",
    longDescription:
      "A scalable multi-tenant platform engineered for university coding clubs and student developer chapters. Features isolated organization tenancy, role-based access control (RBAC), automated competitive programming contest management, real-time leaderboard computations, and member engagement telemetry.",
    category: "Full-Stack SaaS & Cloud",
    status: "In Progress",
    year: "2024 – Present",
    role: "Lead Architect & Full-Stack Developer",
    technologies: ["React.js", "Node.js", "TypeScript", "PostgreSQL", "Docker", "REST APIs", "Tailwind CSS"],
    problem:
      "College coding societies frequently struggle with fragmented tools for contest hosting, member progress tracking, and event management across different academic batches without unified multi-tenant isolation.",
    approach:
      "Architecting a clean multi-tenant modular system with tenant-aware database schemas, automated submission ranking pipelines, and accessible administrative dashboards.",
    architecture:
      "Multi-tenant service architecture: React frontend client -> stateless API gateway -> tenant isolation layer with role-based permissions and PostgreSQL data partitions.",
    engineeringDecisions: [
      {
        decision: "Multi-Tenant Schema Isolation",
        reason: "Ensures clean data boundaries between different college chapters while sharing scalable underlying compute infrastructure."
      },
      {
        decision: "Asynchronous Leaderboard Score Aggregation",
        reason: "Prevents database lock contention during concurrent live contest submissions."
      }
    ],
    engineeringFocus:
      "Multi-tenancy architectures, role-based authorization, scalable database partitioning, and real-time telemetry.",
    highlights: [
      "Multi-organization tenancy with custom chapter domains",
      "Automated contest scheduling and leaderboard evaluation",
      "Member skill telemetry and algorithmic milestone tracking",
      "Modular full-stack TypeScript architecture"
    ],
    lessons:
      "Designing multi-tenant authorization early simplifies database indexing and prevents security cross-contamination across organizations.",
    links: {
      github: null,
      demo: null
    },
    featured: true
  },
  {
    id: "biology-curiosity-book",
    slug: "biology-curiosity-book",
    title: "Biology through Curiosity : Questions that became Knowledge",
    shortDescription: "An in-progress book manuscript exploring how curious questions in natural biology transformed into structured scientific knowledge across genetics, cellular mechanics, and evolution.",
    longDescription:
      "An in-progress book manuscript exploring how intuitive, curious observations of the natural world evolved into formal scientific understanding. Investigating the foundational questions that unlocked cytology, molecular genetics, evolutionary adaptation, cellular automata, and biological information processing. Typeset in LaTeX with custom TikZ vector schemas and structured inquiry models.",
    category: "Book Manuscript & Life Sciences",
    status: "In Progress",
    year: "2024 – Present",
    role: "Author & Researcher",
    technologies: ["LaTeX", "Biology", "Genetics", "Evolutionary Systems", "TikZ", "Book Manuscript"],
    problem:
      "Biological textbooks often present conclusions as dry facts to be memorized, obscuring the fascinating intuitive questions, curiosity, and scientific journeys that led to their discovery in the first place.",
    approach:
      "Structuring a comprehensive narrative in LaTeX centered on first-principles questions—explaining how living systems encode information, replicate state machines, and adapt across generations.",
    architecture:
      "Modular book manuscript in LaTeX with structured chapters on The Architecture of Cells, The Language of Genetics, Evolutionary State Machines, and Modern Biotechnology.",
    engineeringDecisions: [
      {
        decision: "Curiosity-Driven Problem Decomposition",
        reason: "Frames each biological concept around the original historical question that unlocked the underlying mechanism."
      },
      {
        decision: "Vector TikZ Schemas for Biological Pathways",
        reason: "Ensures lossless mathematical precision for molecular cascades, genetic pedigrees, and cellular architectures."
      }
    ],
    engineeringFocus:
      "Biological systems modeling, genetic information encoding, evolutionary mechanisms, LaTeX typography, and TikZ vector schemas.",
    highlights: [
      "Explores canonical biological questions that shaped modern genetics and cytology",
      "Approaches living systems with the clarity of engineering state machines and information theory",
      "Custom vector illustrations typeset in publication-grade LaTeX",
      "Drafted as an accessible yet rigorous book on the epistemology of biology"
    ],
    lessons:
      "Approaching biology through the lens of curiosity and systems thinking transforms dense biochemical pathways into intuitive, logical architectures.",
    links: {
      github: null,
      demo: null
    },
    featured: true
  },
  {
    id: "dsa-handbook",
    slug: "dsa-handbook",
    title: "Data Structures & Algorithms Handbook",
    shortDescription: "A comprehensive reference handbook covering fundamental to advanced data structures and algorithms, typeset in LaTeX.",
    longDescription:
      "A structured, in-depth reference handbook authored for students and software engineers covering essential data structures (arrays, linked lists, trees, heaps, graphs) and algorithmic paradigms (dynamic programming, divide-and-conquer, greedy). Typeset entirely in LaTeX with mathematical rigor, visual memory layouts, and clean C++ implementations.",
    category: "Technical Writing & Core CS",
    status: "Published",
    year: "2023",
    role: "Author & Typesetter",
    technologies: ["LaTeX", "C++", "TikZ", "Technical Writing", "Algorithms"],
    problem:
      "Many DSA resources either lack rigorous mathematical foundations or become overly abstract without concrete code implementations and visual structure diagrams. The goal was to build a comprehensive, mathematically sound, and visual desktop reference.",
    approach:
      "Synthesized core theoretical proofs, asymptotic complexity analyses, and idiomatic C++ code into a unified, publication-grade document formatted with LaTeX and visual structure diagrams.",
    architecture:
      "Modular LaTeX document hierarchy with custom macros for complexity boxes, code listings with syntax highlighting, and TikZ vector diagrams for tree rotations and pointer chains.",
    engineeringDecisions: [
      {
        decision: "Vector TikZ Diagrams for Data Layouts",
        reason: "Avoided raster images to maintain sharp mathematical typography at all zoom levels and print resolutions."
      },
      {
        decision: "Strict C++17 Reference Implementations",
        reason: "Used clean STL types and modern idioms to bridge abstract mathematical definitions with real-world production code."
      }
    ],
    engineeringFocus:
      "Algorithmic precision, complexity proofs, typography standards, document architecture, and visual representations of pointers and graph structures.",
    highlights: [
      "Comprehensive coverage of linear and non-linear data structures",
      "Clean C++ reference implementations with asymptotic bounds",
      "Typeset to academic publishing standards using LaTeX",
      "Accessible reference guide for competitive programming preparation"
    ],
    lessons:
      "Typesetting a large-scale technical book enforced rigorous clarity in explanation—if an algorithm cannot be diagrammed cleanly, the underlying conceptual model is usually muddy.",
    links: {
      github: "https://github.com/Girish-1-Goyal/dsa-handbook",
      demo: "https://drive.google.com/file/d/12CLQ1KWekgsH4rcJUSxIJmH-d_iVN0ae/view?usp=drive_link"
    },
    featured: true
  },
  {
    id: "web-terminal",
    slug: "web-terminal",
    title: "Web Terminal",
    shortDescription: "An interactive browser-based terminal emulator mimicking a Unix CLI environment with custom command parsing.",
    longDescription:
      "Web Terminal is an interactive browser-based terminal application designed to emulate the feel, ergonomics, and responsiveness of a Unix command-line interface directly within the browser. It implements dynamic command interpretation, stateful directory traversal, custom command history, and responsive styling.",
    category: "Systems & Web",
    status: "Completed",
    year: "2023",
    role: "Creator & Lead Developer",
    technologies: ["React.js", "JavaScript", "Python", "Flask", "CSS3"],
    problem:
      "Standard web interfaces can feel abstract or heavy when presenting command-line workflows or developer tools. The challenge was to deliver a responsive, authentic terminal experience in the browser with realistic keystroke parsing, history navigation, and extensible command handlers without bloated dependencies.",
    approach:
      "Engineered a modular command dispatch system using React state and custom hooks. Integrated keyboard event listeners for shell-like interactions (arrow key history navigation, tab completion, clear) and paired it with a lightweight Python/Flask backend utility for command processing.",
    architecture:
      "Client-side React virtual DOM listening to raw key events, maintaining an internal history buffer array, and passing commands to a stateless command dispatcher. Output nodes are rendered linearly with ANSI-compatible styling.",
    engineeringDecisions: [
      {
        decision: "Stateless Command Dispatch Architecture",
        reason: "Decoupled input handling from command execution, allowing new commands to be added as pure functions without modifying terminal render logic."
      },
      {
        decision: "In-Memory Circular Buffer for History",
        reason: "Mirrored POSIX readline behavior for Up/Down arrow navigation without causing memory leaks over long interactive sessions."
      }
    ],
    engineeringFocus:
      "Command parsing algorithms, asynchronous execution queues, terminal layout ergonomics, and keyboard event lifecycle management.",
    highlights: [
      "Interactive shell mimicking Unix terminal behaviors",
      "Command history navigation with Up/Down arrow buffers",
      "Modular command dispatcher supporting custom built-ins",
      "Seamless responsive rendering across desktop and mobile screens"
    ],
    lessons:
      "Building a custom CLI interface revealed the complexity of edge-case keyboard handling in browsers (event bubbling, IME composition, modifier keys) and the value of keeping the state tree strictly unidirectional.",
    links: {
      github: "https://github.com/Girish-1-Goyal/WebTerminal-Project-",
      demo: "https://webterm-u.netlify.app/"
    },
    featured: true
  },
  {
    id: "c-terminal-shell",
    slug: "c-terminal-shell",
    title: "Unix-like Terminal Shell in C",
    shortDescription: "A lightweight command-line interpreter in C implementing process control, system calls, and built-in shell commands.",
    longDescription:
      "A POSIX-compliant minimalist command shell written in C. The shell parses command line input into tokens, handles process creation via fork(), executes binary commands using execvp(), manages child process state with waitpid(), and implements core built-in commands like cd, pwd, and exit.",
    category: "Systems Engineering",
    status: "Completed",
    year: "2022",
    role: "Systems Developer",
    technologies: ["C", "POSIX API", "Linux / Unix System Calls", "Make"],
    problem:
      "Understanding operating system fundamentals requires hands-on exploration of how user space processes interact with the kernel through system calls and process lifecycles.",
    approach:
      "Built the shell from scratch in C with modular functions for reading lines, splitting tokens, resolving paths, handling signal interrupts, and dispatching built-in functions versus child process execution.",
    architecture:
      "Read-Eval-Print Loop (REPL) structure: getline() buffer -> whitespace tokenizer -> built-in router or fork()/execvp() process spawn -> waitpid() status capture.",
    engineeringDecisions: [
      {
        decision: "Zero Third-Party Dependencies",
        reason: "Relied solely on the standard C library and POSIX headers to keep the binary lightweight and portable across POSIX systems."
      },
      {
        decision: "Dynamic Buffer Allocation with Realloc",
        reason: "Allowed arbitrarily long command inputs without fixed-size buffer overflows."
      }
    ],
    engineeringFocus:
      "POSIX system calls (fork, execvp, waitpid), memory safety in C, pointer arithmetic, string tokenization, and signal management.",
    highlights: [
      "Custom line reading and tokenizing engine without heavy third-party dependencies",
      "Robust process lifecycle handling preventing zombie processes",
      "Internal built-in command router for directory traversal and process exit",
      "Low memory footprint and fast execution"
    ],
    lessons:
      "Direct systems programming in C solidified an understanding of process memory spaces, copy-on-write semantics of fork(), and the criticality of disciplined resource cleanup.",
    links: {
      github: "https://github.com/Girish-1-Goyal/Terminal-C-Simple",
      demo: null
    },
    featured: true
  },
  {
    id: "personal-portfolio-platform",
    slug: "personal-portfolio-platform",
    title: "Editorial Portfolio & Digital Archive",
    shortDescription: "A statically generated personal website, writing space, and digital library built with React and clean design tokens.",
    longDescription:
      "A modern, route-based static personal platform designed to showcase software engineering, book writing, a scalable digital bookshelf, and intellectual exploration. Engineered without database or authentication overhead for peak performance, security, and editorial elegance.",
    category: "Web & Architecture",
    status: "Completed",
    year: "2024 – 2026",
    role: "Architect & Designer",
    technologies: ["React.js", "React Router", "Material UI", "Framer Motion", "Markdown"],
    problem:
      "Traditional developer portfolios often rely on generic templates, excessive animations, and backend complexity that provide little editorial substance or longevity.",
    approach:
      "Architected a pure static data layer with route-level separation for work, writing, reading, and journey, paired with clean typography and responsive design tokens.",
    architecture:
      "Static single-page React app with centralized JS data modules, tokenized Material-UI theme provider, and zero server-side computation at runtime.",
    engineeringDecisions: [
      {
        decision: "Zero-Database Static Architecture",
        reason: "Guarantees zero database outages, zero backend security vulnerabilities, and instantaneous page transitions."
      },
      {
        decision: "Design Token Centralization",
        reason: "Separated visual tokens (colors, typography, radii, spacing) from component rendering logic for seamless theme maintenance."
      }
    ],
    engineeringFocus:
      "Static generation, minimal dependency surface, route-based architecture, client-side accessibility, and editorial typography.",
    highlights: [
      "Full route-based navigation with instant client-side transitions",
      "Scalable static bookshelf data model ready for 1,000+ titles",
      "Zero backend attack surface with strict static generation",
      "Responsive layout optimized across mobile, tablet, and desktop"
    ],
    lessons:
      "Static-first architecture is not a limitation—it is a superpower for personal archives and content platforms that require longevity and speed.",
    links: {
      github: "https://github.com/Girish-1-Goyal/personal-portfolio-React-",
      demo: "https://girishgoyal.netlify.app/"
    },
    featured: false
  }
];

export const experiments = [
  {
    id: "competitive-programming-templates",
    title: "Competitive Programming C++ Template Library",
    year: "2023 – Present",
    category: "Algorithms",
    description: "A lightweight collection of modular C++ templates for fast I/O, modular arithmetic, disjoint set union (DSU), segment trees, and binary lifting.",
    technologies: ["C++20", "Data Structures", "Algorithms", "Codeforces"],
    status: "Active"
  },
  {
    id: "tikz-memory-visualizers",
    title: "LaTeX TikZ Memory Layout & Graph Macros",
    year: "2023",
    category: "Typesetting & Tooling",
    description: "Custom LaTeX TikZ macro package for rendering memory pointers, doubly-linked lists, and binary search tree rebalancing operations.",
    technologies: ["LaTeX", "TikZ", "Computer Graphics", "Publishing"],
    status: "Completed"
  },
  {
    id: "posix-signal-traps",
    title: "POSIX Process & Signal Traps Explorer",
    year: "2022",
    category: "Systems",
    description: "Test harnesses and minimal C scripts exploring child process signal masking, zombie reaping with SIGCHLD, and IPC pipe buffering.",
    technologies: ["C", "Linux", "POSIX Signals", "IPC"],
    status: "Completed"
  }
];

export const archivedProjects = [
  {
    id: "knoldus-frontend-internship",
    title: "Client Dashboard & Team Panels (Internship)",
    year: "2021 – 2022",
    category: "Web Development",
    description: "Developed UI panels, API integration handlers, and collaborative components using Angular and TypeScript during software internship at Knoldus.",
    technologies: ["Angular", "TypeScript", "REST APIs", "Git"],
    role: "Frontend Developer Intern",
    status: "Archived"
  }
];

export const stealthStartup = {
  id: "stealth-startup",
  title: "Independent Software & Systems (Stealth)",
  overview: "Designing and prototyping independent software products focused on deterministic developer tools, minimal dependency architectures, and local-first workflows.",
  status: "In Progress",
  timeline: "2024 – Present",
  route: "/work/building"
};
